'use client'

import { ActorRefFrom, assign, createMachine, fromCallback } from 'xstate'

import { Suburbs } from '@/data/getSuburbs'
import { createActorContext } from '@/utils/createActorContext'
import { machineTypes } from '@/utils/machineTypes'
import { ShowcaseContext, type ShowcaseMachine } from '../Showcase/ShowcaseContext'
import { SuburbSelectorProvider } from '../SuburbSelector/SuburbSelectorContext'

const searchHeroMachine =
  /** @xstate-layout N4IgpgJg5mDOIC5QGUwEMBOBjAFgCTAwHsA6ASQDsBLAFyrQBsACWGtGsAYgEEs6A3dmBYBXAEYiMYlumw4A2gAYAuolAAHIrFpUiFNSAAeiALQBGRQFYSlgGwAmSwBoQAT1MAOeyVsBOX-ZmHgDslgC+YS6omLgExOTUdIwsbBw8fFSCHEwUYFRQOGJEkjhERBBMYsQA7rCESqpIIJradHoGxghmwR4kwQG+tiHObqYAzN5jACzBtnaOEVGysYSk0XKiElKwnABKYDSSFEw0RExUifTMrEINBi067U2d5oq2fYrBimNmji7uCHs9l8NkWIHWK3iACEanUcnkCkUSmUIDt9ocMMdTudLskbhw7k0Hm19M9EEDvD1LGZ5iMAYEpjYpsChqEIpEQBRynADBD8Kt7lpHqTQC8pmMxiQxsELL5pXTTN1FCQzP4gYpfL97NLBmC+XFSJQdHjUmBBa1dCKjIgQn0prYNfL-ogLN5FN9fuEOfrViQ+ZtJGJ4EShSSOuMPNZpbKnaMEHM+oFhu6U+6zHrlvzobDhLl8oVihhSuVgxpQ5bw4DfMrLL5ZrTnQgeomgqFUymxuywkA */
  createMachine(
    {
      id: 'SearchHero',
      types: machineTypes<{
        context: {
          searchInput: HTMLInputElement | null
          showcaseRef: ActorRefFrom<ShowcaseMachine> | null
        }
        input: Partial<{
          searchInput: HTMLInputElement | null
          showcaseRef: ActorRefFrom<ShowcaseMachine> | null
        }>
        events:
          | { type: 'Activate suburb search'; searchInput: HTMLInputElement }
          | { type: 'Activate neighbourhood browser' }
          | { type: 'Return to initial state' }
      }>(),

      context: ({ input }) => ({
        searchInput: null,
        showcaseRef: null,
        ...input,
      }),

      initial: 'Initial state',

      states: {
        'Initial state': {
          on: {
            'Activate suburb search': {
              target: 'Search suburbs',
              actions: ['Set search field', 'Dim neighbourhood browser'],
            },
            'Activate neighbourhood browser': 'Browse neighbourhoods',
          },
        },

        'Search suburbs': {
          on: {
            'Return to initial state': {
              target: 'Initial state',
              actions: 'Reveal neighbourhood browser',
            },
          },

          invoke: {
            src: 'Listen for escape key',
            id: 'escapeKeyListener',
          },

          exit: 'Blur search field',
        },
        'Browse neighbourhoods': {
          invoke: {
            src: 'Listen for escape key',
            id: 'escapeKeyListener',
          },
          on: {
            'Return to initial state': 'Initial state',
          },
        },
      },
    },
    {
      actions: {
        'Set search field': assign(({ event }) => {
          if (event.type !== 'Activate suburb search') {
            return {}
          }

          return { searchInput: event.searchInput }
        }),
        'Blur search field': ({ context }) => {
          context.searchInput?.blur()
        },
        'Dim neighbourhood browser': ({ context }) => context.showcaseRef?.send({ type: 'Dim showcase', dimmed: true }),
        'Reveal neighbourhood browser': ({ context }) =>
          context.showcaseRef?.send({ type: 'Dim showcase', dimmed: false }),
      },
      actors: {
        'Listen for escape key': fromCallback(({ sendBack }) => {
          const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
              sendBack({ type: 'Return to initial state' })
            }
          }

          document.addEventListener('keydown', handleKeyDown)

          return () => {
            document.removeEventListener('keydown', handleKeyDown)
          }
        }),
      },
    },
  )

export const SearchHeroContext = createActorContext(searchHeroMachine)

export function SearchHeroProvider({ children, suburbs }: { children: React.ReactNode; suburbs: Suburbs }) {
  const showcaseRef = ShowcaseContext.useActorRef()
  return (
    <SuburbSelectorProvider suburbs={suburbs}>
      <SearchHeroContext.Provider options={{ input: { searchInput: null, showcaseRef } }}>
        {children}
      </SearchHeroContext.Provider>
    </SuburbSelectorProvider>
  )
}
